import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Key,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Building,
  Mail,
  MapPin,
  User,
  Eye,
  EyeOff,
  CreditCard,
  Clock,
  FileText,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import api from '../lib/api';
import { EmptyState } from '../components/EmptyState';

interface BillingData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  nip: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

// ─── Tab: Moje konto ──────────────────────────────────────────
function MyAccountTab() {
  const { user, updateUser } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await api.put('/account/billing', formData);
      updateUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      setSuccess('Dane konta zostały zapisane');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się zapisać danych');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center p-4 bg-success/10 border border-success/30 rounded-xl text-success">
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <div className="card">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-accent-primary" />
            Dane osobowe
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Imię</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
                placeholder="Jan"
              />
            </div>
            <div>
              <label className="label">Nazwisko</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
                placeholder="Kowalski"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="label">
              <Mail className="w-4 h-4 inline mr-1" />
              Adres e-mail
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              placeholder="jan.kowalski@email.pl"
            />
          </div>
        </div>

        <div className="flex justify-start">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? (
              <span className="flex items-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Zapisywanie...
              </span>
            ) : (
              <span className="flex items-center">
                <Save className="w-5 h-5 mr-2" />
                Zapisz dane
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Tab: Dane rozliczeniowe ──────────────────────────────────
function BillingTab() {
  const { tenant } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState<Omit<BillingData, 'firstName' | 'lastName' | 'email'>>({
    companyName: tenant?.name || '',
    nip: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Polska',
  });

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<{ billingData?: BillingData }>('/account/billing');
      if (data.billingData) {
        setFormData({
          companyName: data.billingData.companyName || tenant?.name || '',
          nip: data.billingData.nip || '',
          street: data.billingData.street || '',
          postalCode: data.billingData.postalCode || '',
          city: data.billingData.city || '',
          country: data.billingData.country || 'Polska',
        });
      }
    } catch {
      // Billing data may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await api.put('/account/billing', formData);
      setSuccess('Dane rozliczeniowe zostały zapisane');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się zapisać danych');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center p-4 bg-success/10 border border-success/30 rounded-xl text-success">
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <div className="card">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-accent-secondary" />
            Dane firmy
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Nazwa firmy</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="input"
                placeholder="Nazwa Firmy Sp. z o.o."
              />
            </div>
            <div>
              <label className="label">NIP</label>
              <input
                type="text"
                value={formData.nip}
                onChange={(e) => setFormData({ ...formData, nip: e.target.value })}
                className="input"
                placeholder="1234567890"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-success" />
            Adres firmy
          </h3>
          <div className="space-y-4">
            <div>
              <label className="label">Ulica i numer</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="input"
                placeholder="ul. Przykładowa 123/4"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Kod pocztowy</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  className="input"
                  placeholder="00-000"
                />
              </div>
              <div>
                <label className="label">Miasto</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input"
                  placeholder="Warszawa"
                />
              </div>
              <div>
                <label className="label">Kraj</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="input"
                  placeholder="Polska"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-start">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? (
              <span className="flex items-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Zapisywanie...
              </span>
            ) : (
              <span className="flex items-center">
                <Save className="w-5 h-5 mr-2" />
                Zapisz dane
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Tab: Historia płatności ──────────────────────────────────
function PaymentHistoryTab() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-accent-primary" />
          Historia płatności
        </h3>

        {/* Current plan info */}
        <div className="bg-surface-hover/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Aktualny plan</p>
              <p className="text-lg font-semibold text-text-primary">BETA</p>
            </div>
            <span className="px-3 py-1 bg-success/20 text-success text-xs font-medium rounded-full border border-success/30">
              Aktywny
            </span>
          </div>
        </div>

        {/* Empty state */}
        <EmptyState
          icon={FileText}
          title="Brak historii płatności"
          description="Historia płatności pojawi się po pierwszej transakcji"
          size="sm"
        />
      </div>
    </div>
  );
}

// ─── Tab: Zmiana hasła ────────────────────────────────────────
function PasswordTab() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Nowe hasła nie są identyczne');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Nowe hasło musi mieć co najmniej 8 znaków');
      return;
    }

    setSaving(true);

    try {
      await api.put('/account/password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      setSuccess('Hasło zostało zmienione');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się zmienić hasła');
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center p-4 bg-success/10 border border-success/30 rounded-xl text-success">
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <div className="card">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-accent-primary" />
            Zmień hasło
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Aktualne hasło</label>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  className="input pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                >
                  {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">Nowe hasło</label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  className="input pr-10"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                >
                  {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-text-muted mt-1">Minimum 8 znaków</p>
            </div>

            <div>
              <label className="label">Potwierdź nowe hasło</label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="input pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                >
                  {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-start">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? (
              <span className="flex items-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Zapisywanie...
              </span>
            ) : (
              <span className="flex items-center">
                <Key className="w-5 h-5 mr-2" />
                Zmień hasło
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Main Account Page with Tabs ──────────────────────────────
const tabs = [
  { id: 'account', label: 'Moje konto', icon: User },
  { id: 'billing', label: 'Dane rozliczeniowe', icon: CreditCard },
  { id: 'payments', label: 'Historia płatności', icon: Clock },
  { id: 'password', label: 'Zmiana hasła', icon: Key },
] as const;

type TabId = typeof tabs[number]['id'];

export function AccountPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabId) || 'account';

  const setActiveTab = (tab: TabId) => {
    setSearchParams({ tab });
  };

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Moje konto</h1>
        <p className="text-text-secondary mt-1">Zarządzaj swoim kontem i danymi rozliczeniowymi</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-8 bg-surface-raised/40 p-1 rounded-xl border border-border-subtle overflow-x-auto">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/30'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'account' && <MyAccountTab />}
      {activeTab === 'billing' && <BillingTab />}
      {activeTab === 'payments' && <PaymentHistoryTab />}
      {activeTab === 'password' && <PasswordTab />}
    </div>
  );
}
